import { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { ActionFunctionArgs, MetaFunction } from 'react-router';
import { useFetcher } from 'react-router';

import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle, Mail, ArrowLeft } from 'lucide-react';

import Header from '~/components/Header';
import Footer from '~/components/Footer';
import { usePostHog, EVENTS } from '~/hooks/usePostHog';
import { prisma } from '~/utils/db.server';
import { emailService } from '~/utils/email.server';
import { createLogger } from '~/utils/logger.server';

const logger = createLogger('Quiz');

const PROMO_CODE = 'LEVELUP20';

export const meta: MetaFunction = () => [
  { title: 'Interview Readiness Quiz | Pavone' },
  { name: 'description', content: 'Take our quick quiz to discover your interview readiness score and get personalized tips to improve.' },
];

// --- Server Action ---

interface QuizAnswers {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
}

function computeScore(answers: QuizAnswers): number {
  let score = 50;

  // Q1: How many interviews coming up?
  if (answers.q1 === 'active') score += 10;
  else if (answers.q1 === '3+') score += 5;
  else if (answers.q1 === '1-2') score += 0;
  else score -= 5; // none

  // Q2: Confidence level
  if (answers.q2 === 'very') score += 15;
  else if (answers.q2 === 'somewhat') score += 5;
  else if (answers.q2 === 'not-very') score -= 5;
  else score -= 10; // not-at-all

  // Q3: Practice out loud?
  if (answers.q3 === 'always') score += 15;
  else if (answers.q3 === 'sometimes') score += 5;
  else if (answers.q3 === 'rarely') score -= 5;
  else score -= 10; // never

  // Q4: Biggest challenge (no penalty, just shifts)
  if (answers.q4 === 'structuring') score -= 3;
  else if (answers.q4 === 'nerves') score -= 5;
  else if (answers.q4 === 'clarity') score -= 3;
  else score -= 2; // thinking

  return Math.max(15, Math.min(95, score));
}

function generateTips(answers: QuizAnswers): string[] {
  const tips: string[] = [];

  // Based on confidence
  if (answers.q2 === 'not-at-all' || answers.q2 === 'not-very') {
    tips.push('Record yourself answering common questions — hearing your own voice builds familiarity and reduces anxiety.');
  }

  // Based on practice habits
  if (answers.q3 === 'never' || answers.q3 === 'rarely') {
    tips.push('Practice out loud at least 3 times before your interview. Silent rehearsal misses 60% of delivery issues.');
  } else if (answers.q3 === 'sometimes') {
    tips.push('Make practice a daily habit — even 5 minutes of speaking out loud compounds into major improvement.');
  }

  // Based on challenge
  if (answers.q4 === 'structuring') {
    tips.push('Use the STAR method (Situation, Task, Action, Result) to give your answers a clear structure.');
  } else if (answers.q4 === 'nerves') {
    tips.push('Try the 4-7-8 breathing technique before your interview: inhale 4s, hold 7s, exhale 8s. It calms your nervous system.');
  } else if (answers.q4 === 'clarity') {
    tips.push('Slow down your speaking pace by 20% — most people speak too fast when nervous, which hurts clarity.');
  } else {
    tips.push('Prepare 2-3 versatile stories from your experience that can be adapted to different question types.');
  }

  // Generic high-value tip if we have room
  if (tips.length < 3) {
    tips.push('Focus on specific numbers and results when describing your experience — concrete examples are 3x more memorable.');
  }
  if (tips.length < 3) {
    tips.push('End each answer with a forward-looking statement about how your skills apply to the new role.');
  }

  return tips.slice(0, 3);
}

export async function action({ request }: ActionFunctionArgs) {
  if (request.method !== 'POST') {
    return Response.json({ error: 'Method not allowed' }, { status: 405 });
  }

  try {
    const body = await request.json();
    const { email, name, answers, locale } = body as {
      email: string;
      name?: string;
      answers: QuizAnswers;
      locale?: string;
    };

    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return Response.json({ error: 'Please enter a valid email address' }, { status: 400 });
    }

    // Validate answers
    if (!answers?.q1 || !answers?.q2 || !answers?.q3 || !answers?.q4) {
      return Response.json({ error: 'Please answer all questions' }, { status: 400 });
    }

    const score = computeScore(answers);
    const tips = generateTips(answers);

    // Save to database
    await prisma.quizSubmission.create({
      data: {
        email,
        name: name || null,
        answers,
        score,
        locale: locale || 'en',
      },
    });

    // Send results email
    const emailResult = await emailService.sendQuizResultsEmail({
      userEmail: email,
      userName: name || '',
      readinessScore: score,
      tips,
      promoCode: PROMO_CODE,
    });

    if (!emailResult.success) {
      logger.error('Failed to send quiz results email', {
        metadata: { email, error: emailResult.error },
      });
      return Response.json({ error: 'Failed to send email. Please try again.' }, { status: 500 });
    }

    logger.info('Quiz completed', {
      metadata: { email, score, locale: locale || 'en' },
    });

    return Response.json({ success: true, email });
  } catch (error) {
    logger.error('Quiz submission error', {
      metadata: { error: error instanceof Error ? error.message : String(error) },
    });
    return Response.json({ error: 'Something went wrong. Please try again.' }, { status: 500 });
  }
}

// --- Quiz Questions Config ---

interface QuizOption {
  value: string;
  label: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
}

// --- Client Component ---

export default function QuizPage() {
  const { _ } = useLingui();
  const fetcher = useFetcher();
  const { track } = usePostHog();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  // Track quiz started on mount
  useEffect(() => {
    track(EVENTS.QUIZ_STARTED);
  }, []);

  // Track quiz completed when server returns success
  useEffect(() => {
    if (fetcher.data && 'success' in fetcher.data && fetcher.data.success) {
      track(EVENTS.QUIZ_COMPLETED, { answers });
    }
  }, [fetcher.data]);

  const questions: QuizQuestion[] = [
    {
      id: 'q1',
      question: _(msg`How many interviews do you have coming up?`),
      options: [
        { value: 'none', label: _(msg`None yet`) },
        { value: '1-2', label: _(msg`1-2 interviews`) },
        { value: '3+', label: _(msg`3 or more`) },
        { value: 'active', label: _(msg`I'm actively interviewing`) },
      ],
    },
    {
      id: 'q2',
      question: _(msg`How confident are you in your interview answers?`),
      options: [
        { value: 'very', label: _(msg`Very confident`) },
        { value: 'somewhat', label: _(msg`Somewhat confident`) },
        { value: 'not-very', label: _(msg`Not very confident`) },
        { value: 'not-at-all', label: _(msg`Not confident at all`) },
      ],
    },
    {
      id: 'q3',
      question: _(msg`Do you practice answers out loud before interviews?`),
      options: [
        { value: 'always', label: _(msg`Always`) },
        { value: 'sometimes', label: _(msg`Sometimes`) },
        { value: 'rarely', label: _(msg`Rarely`) },
        { value: 'never', label: _(msg`Never`) },
      ],
    },
    {
      id: 'q4',
      question: _(msg`What's your biggest interview challenge?`),
      options: [
        { value: 'structuring', label: _(msg`Structuring my answers`) },
        { value: 'nerves', label: _(msg`Managing nerves`) },
        { value: 'clarity', label: _(msg`Speaking clearly`) },
        { value: 'thinking', label: _(msg`Thinking on my feet`) },
      ],
    },
  ];

  const totalSteps = questions.length + 1; // questions + email step
  const isEmailStep = currentStep === questions.length;
  const isSubmitted = fetcher.data && 'success' in fetcher.data && fetcher.data.success;
  const isSubmitting = fetcher.state === 'submitting';
  const serverError = fetcher.data && 'error' in fetcher.data ? (fetcher.data as { error: string }).error : null;

  const handleSelectOption = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    track(EVENTS.QUIZ_QUESTION_ANSWERED, {
      question_id: questionId,
      question_number: currentStep + 1,
      answer: value,
    });
    // Auto-advance to next step
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
    }, 300);
  };

  const handleSubmit = () => {
    if (!email) return;
    const locale = document.documentElement.lang || 'en';
    fetcher.submit(
      JSON.stringify({ email, name, answers, locale }),
      {
        method: 'POST',
        encType: 'application/json',
      }
    );
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative bg-white">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-16 sm:py-24">
        <div className="w-full max-w-lg">

          {/* Success State */}
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-brand-dark-blue">
                <Trans>Check your inbox!</Trans>
              </h1>
              <p className="text-gray-600 text-base sm:text-lg">
                <Trans>
                  We've sent your Interview Readiness results and a special offer to <strong>{email}</strong>
                </Trans>
              </p>
              <a
                href="/"
                className="inline-block mt-4 px-6 py-3 bg-brand-dark-blue text-white rounded-lg font-semibold hover:bg-brand-dark-blue/90 transition-colors"
              >
                <Trans>Back to home</Trans>
              </a>
            </motion.div>
          ) : (
            <>
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">
                    {isEmailStep ? (
                      <Trans>Almost done!</Trans>
                    ) : (
                      <Trans>Question {currentStep + 1} of {questions.length}</Trans>
                    )}
                  </span>
                  {currentStep > 0 && (
                    <button
                      onClick={handleBack}
                      className="text-sm text-brand-dark-blue hover:text-brand-dark-blue/70 flex items-center gap-1 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      <Trans>Back</Trans>
                    </button>
                  )}
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-brand-dark-blue rounded-full"
                    initial={false}
                    animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Question / Email Step */}
              <AnimatePresence mode="wait">
                {!isEmailStep ? (
                  <motion.div
                    key={`question-${currentStep}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25 }}
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-brand-dark-blue mb-6">
                      {questions[currentStep].question}
                    </h2>
                    <div className="space-y-3">
                      {questions[currentStep].options.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSelectOption(questions[currentStep].id, option.value)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between group ${
                            answers[questions[currentStep].id] === option.value
                              ? 'border-brand-dark-blue bg-brand-dark-blue/5'
                              : 'border-gray-200 hover:border-brand-dark-blue/40 hover:bg-gray-50'
                          }`}
                        >
                          <span className="text-base font-medium text-brand-dark-blue">
                            {option.label}
                          </span>
                          <ChevronRight className={`w-5 h-5 transition-transform ${
                            answers[questions[currentStep].id] === option.value
                              ? 'text-brand-dark-blue translate-x-0'
                              : 'text-gray-300 -translate-x-1 group-hover:translate-x-0 group-hover:text-gray-400'
                          }`} />
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="email-step"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-6"
                  >
                    <div className="text-center mb-2">
                      <div className="w-12 h-12 mx-auto rounded-full bg-brand-dark-blue/10 flex items-center justify-center mb-4">
                        <Mail className="w-6 h-6 text-brand-dark-blue" />
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-brand-dark-blue">
                        <Trans>Your results are ready!</Trans>
                      </h2>
                      <p className="text-gray-600 mt-2">
                        <Trans>Enter your email to receive your personalized readiness score, tips, and a special discount.</Trans>
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="quiz-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                          <Trans>Email</Trans> *
                        </label>
                        <input
                          id="quiz-email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                          placeholder={_(msg`your@email.com`)}
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand-dark-blue focus:outline-none transition-colors text-base"
                        />
                      </div>
                      <div>
                        <label htmlFor="quiz-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                          <Trans>Name</Trans> <span className="text-gray-400">(<Trans>optional</Trans>)</span>
                        </label>
                        <input
                          id="quiz-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                          placeholder={_(msg`Your name`)}
                          className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-brand-dark-blue focus:outline-none transition-colors text-base"
                        />
                      </div>
                    </div>

                    {serverError && (
                      <p className="text-red-600 text-sm text-center">{serverError}</p>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={!email || isSubmitting}
                      className="w-full py-3.5 bg-brand-dark-blue text-white rounded-xl font-semibold text-base hover:bg-brand-dark-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <Trans>Sending...</Trans>
                      ) : (
                        <Trans>Get My Results</Trans>
                      )}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      <Trans>We'll send your results and won't spam you. Unsubscribe anytime.</Trans>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
